import * as React from 'react'
import { ChevronDown } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { getJSON, patchJSON, postJSON } from '@/lib/api'

type Factor =
  | 'membership_tenure'
  | 'event_attendance'
  | 'portal_presence'
  | 'email_engagement'
  | 'giving'
type Points = Record<Factor, number>
type Audit = {
  id: number
  saved_by: string
  saved_at: string
  old_points: Points
  new_points: Points
}
type Response = { engagement_score_weights: Points & { audit_history?: Audit[]; stale?: boolean } }
type Preview = { current: Record<string, number>; preview: Record<string, number> }

const FACTORS: Array<{ key: Factor; name: string; description: string }> = [
  {
    key: 'membership_tenure',
    name: 'Membership tenure',
    description: 'Time since the member first became Active.',
  },
  {
    key: 'event_attendance',
    name: 'Event attendance',
    description: 'Check-ins at events during the scoring window.',
  },
  {
    key: 'portal_presence',
    name: 'Portal presence',
    description: 'Recent visits to the Member Portal.',
  },
  {
    key: 'email_engagement',
    name: 'Email engagement',
    description: 'Clicks on association email.',
  },
  { key: 'giving', name: 'Giving', description: 'Gifts made to the association.' },
]
const DEFAULTS: Points = {
  membership_tenure: 30,
  event_attendance: 25,
  portal_presence: 20,
  email_engagement: 15,
  giving: 10,
}
const API_PATH = '/api/v1/engagement_score_weights'

function samePoints(a: Points, b: Points) {
  return FACTORS.every(({ key }) => a[key] === b[key])
}
function pointsFrom(response: Response): Points {
  const points = response.engagement_score_weights
  return Object.fromEntries(FACTORS.map(({ key }) => [key, Number(points[key])])) as Points
}
function errorMessage(error: unknown): string | null {
  if (!(error instanceof Error)) return null
  const match = error.message.match(/at least two|total 100/i)
  return match
    ? match[0].toLowerCase().includes('two')
      ? 'At least two factors must be counted.'
      : 'Points must total 100.'
    : error.message
}

function WeightsEditor({
  draft,
  total,
  balanced,
  onChange,
}: {
  draft: Points
  total: number
  balanced: boolean
  onChange: (key: Factor, value: number) => void
}) {
  return (
    <Card>
      <CardContent className="p-0">
        <div className="flex justify-end px-5 pb-1 pt-4">
          <span className="w-20 text-right text-caption text-muted-foreground">Points</span>
        </div>
        <div className="divide-y divide-border">
          {FACTORS.map(({ key, name, description }) => (
            <div
              className={`flex items-center gap-3 px-5 py-3 ${draft[key] === 0 ? 'opacity-50' : ''}`}
              key={key}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-body-sm font-medium text-foreground">{name}</span>
                  <span className="text-caption text-muted-foreground">
                    {total ? Math.round((draft[key] / total) * 100) : 0}%
                  </span>
                </div>
                <p className="text-caption text-muted-foreground">
                  {draft[key] === 0 ? 'Not counted' : description}
                </p>
              </div>
              <Input
                aria-label={`${name} points`}
                className="h-8 w-20 text-right tabular-nums"
                min={0}
                type="number"
                value={draft[key]}
                onChange={(event) => onChange(key, Math.max(0, Number(event.target.value) || 0))}
              />
            </div>
          ))}
          <div className="flex items-center justify-between px-5 py-3">
            <div className="flex items-center gap-2">
              <span className="text-body-sm font-semibold text-foreground">Total</span>
              {!balanced && (
                <span className="text-caption text-muted-foreground">
                  {total > 100 ? `${total - 100} points over` : `${100 - total} points short`}
                </span>
              )}
            </div>
            <span
              className={`text-body-sm font-semibold tabular-nums ${balanced ? 'text-success' : 'text-destructive'}`}
            >
              {total} / 100
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function ImpactPreview({ preview }: { preview?: Preview }) {
  const rows = Object.keys(preview?.current ?? {}).map((band) => ({
    band,
    now: preview?.current[band] ?? 0,
    after: preview?.preview[band] ?? 0,
  }))
  return (
    <Card className="mt-6">
      <div className="border-b border-border px-5 py-4">
        <h2 className="text-body font-medium text-foreground">If you save this</h2>
        <p className="text-caption text-muted-foreground">Recomputed from stored sub-scores</p>
      </div>
      <div className="px-5 py-4">
        <Table className="max-w-md">
          <TableHeader>
            <TableRow>
              <TableHead className="h-8 px-3 py-1.5">Band</TableHead>
              <TableHead className="h-8 px-3 py-1.5 text-right">Now</TableHead>
              <TableHead className="h-8 px-3 py-1.5 text-right">After saving</TableHead>
              <TableHead className="h-8 px-3 py-1.5 text-right">Change</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(({ band, now, after }) => (
              <TableRow key={band}>
                <TableCell className="px-3 py-1.5">{band}</TableCell>
                <TableCell className="px-3 py-1.5 text-right tabular-nums">{now}</TableCell>
                <TableCell className="px-3 py-1.5 text-right tabular-nums">{after}</TableCell>
                <TableCell className="px-3 py-1.5 text-right tabular-nums">
                  {after - now > 0 ? '+' : ''}
                  {after - now}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  )
}

function ChangeHistory({ entries }: { entries: Audit[] }) {
  const [open, setOpen] = React.useState(false)
  return (
    <Collapsible className="mt-6" open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex items-center gap-1.5 text-body-sm font-medium">
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        Change history
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-3 divide-y divide-border">
        {entries.map((entry) => (
          <div className="py-3" key={entry.id}>
            <div className="text-body-sm font-medium">
              {entry.saved_by}{' '}
              <span className="text-caption font-normal text-muted-foreground">
                {new Date(entry.saved_at).toLocaleString()}
              </span>
            </div>
            <div className="text-caption text-muted-foreground">
              {FACTORS.map(
                ({ key, name }) => `${name}: ${entry.old_points[key]} → ${entry.new_points[key]}`,
              ).join(' · ')}
            </div>
          </div>
        ))}
      </CollapsibleContent>
    </Collapsible>
  )
}

// The page coordinates several independent query/mutation states and their UI branches.
// eslint-disable-next-line complexity
export function EngagementWeightsPage() {
  const queryClient = useQueryClient()
  const weightsQuery = useQuery({
    queryKey: ['engagement-score-weights'],
    queryFn: () => getJSON<Response>(API_PATH),
  })
  const saved = weightsQuery.data ? pointsFrom(weightsQuery.data) : DEFAULTS
  const [draft, setDraft] = React.useState<Points>(saved)
  React.useEffect(() => {
    if (weightsQuery.data) setDraft(pointsFrom(weightsQuery.data))
  }, [weightsQuery.data])
  const previewMutation = useMutation({
    mutationFn: (points: Points) =>
      postJSON<Preview>(`${API_PATH}/preview`, { engagement_score_weights: points }),
  })
  const previewWeights = previewMutation.mutate
  const saveMutation = useMutation({
    mutationFn: (points: Points) =>
      patchJSON<Response>(API_PATH, { engagement_score_weights: points }),
    onSuccess: (data) => {
      queryClient.setQueryData(['engagement-score-weights'], data)
      setDraft(pointsFrom(data))
      void previewMutation.mutateAsync(pointsFrom(data))
    },
  })
  const resetMutation = useMutation({
    mutationFn: () => postJSON<Response>(`${API_PATH}/reset`, {}),
    onSuccess: (data) => {
      queryClient.setQueryData(['engagement-score-weights'], data)
      setDraft(pointsFrom(data))
      previewMutation.reset()
    },
  })
  const total = FACTORS.reduce((sum, { key }) => sum + (draft[key] || 0), 0)
  const counted = FACTORS.filter(({ key }) => draft[key] > 0).length
  const unchanged = samePoints(draft, saved)
  const balanced = total === 100
  const saveReason = unchanged
    ? null
    : !balanced
      ? 'Points must total 100.'
      : counted < 2
        ? 'At least two factors must be counted.'
        : null
  React.useEffect(() => {
    if (balanced && counted >= 2) previewWeights(draft)
  }, [balanced, counted, draft, unchanged, previewWeights])
  const response = weightsQuery.data?.engagement_score_weights
  const history = response?.audit_history ?? []
  const preview = previewMutation.data
  if (weightsQuery.isLoading || weightsQuery.isError)
    return weightsQuery.isLoading ? (
      <p className="text-body-sm text-muted-foreground">Loading engagement weights...</p>
    ) : (
      <Alert>Engagement weights unavailable</Alert>
    )
  return (
    <div className="mx-auto w-full max-w-[1200px] px-6 py-6">
      <div className="max-w-3xl">
        <header className="pb-4">
          <h1 className="text-heading-sm font-semibold text-foreground">Engagement weights</h1>
          <p className="mt-1 text-body-sm text-muted-foreground">
            Points each factor contributes to every member&apos;s engagement score.
          </p>
          <p className="mt-2 text-caption text-muted-foreground">
            Engaged 60–100 · Drifting 25–59 · Disengaged 0–24
          </p>
        </header>
        {response?.stale && (
          <Alert className="mb-4">The newest Engagement Score is more than 48 hours old.</Alert>
        )}
        <WeightsEditor
          draft={draft}
          total={total}
          balanced={balanced}
          onChange={(key, value) => setDraft({ ...draft, [key]: value })}
        />
        <ImpactPreview preview={preview} />
        <div className="mt-6 flex items-center gap-3">
          <Button
            disabled={Boolean(saveReason) || saveMutation.isPending}
            onClick={() => saveMutation.mutate(draft)}
          >
            Save
          </Button>
          <Button variant="outline" onClick={() => resetMutation.mutate()}>
            Reset to defaults
          </Button>
          {saveReason && <span className="text-caption text-muted-foreground">{saveReason}</span>}
          {saveMutation.isError && (
            <span className="text-caption text-destructive">
              {errorMessage(saveMutation.error)}
            </span>
          )}
        </div>
        <ChangeHistory entries={[...history].reverse()} />
      </div>
    </div>
  )
}
